import { useState, useEffect } from 'react';

export default function Hero() {
  const [currentFeature, setCurrentFeature] = useState(0);
  const features = [
    { icon: 'ri-car-line', text: 'Large choix de véhicules' },
    { icon: 'ri-money-dollar-circle-line', text: 'Prix abordables' },
    { icon: 'ri-map-pin-line', text: 'Disponible à Taza' },
    { icon: 'ri-time-line', text: 'Réservation instantanée' }
  ];

  const contacts = [
    {
      icon: 'ri-whatsapp-line',
      color: 'from-green-500 to-green-600',
      link: 'https://wa.me/212667304231'
    },
    {
      icon: 'ri-tiktok-line',
      color: 'from-gray-800 to-black',
      link: 'https://www.tiktok.com/@urban.drive.cars'
    },
    {
      icon: 'ri-facebook-circle-line',
      color: 'from-[#fe7222] to-[#ff8c4a]',
      link: 'https://www.facebook.com/profile.php?id=61559900651568#'
    },
    {
      icon: 'ri-instagram-line',
      color: 'from-pink-500 via-purple-500 to-orange-500',
      link: 'https://www.instagram.com/Urban.drive.cars/'
    },
    {
      icon: 'ri-phone-line',
      color: 'from-[#fe7222] to-[#ff8c4a]',
      link: 'tel:+212667304231'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative h-screen w-full flex items-center justify-center overflow-hidden">
      {/* Gradient Overlay for Text Readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80 pointer-events-none"></div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-16 sm:pt-32">
        <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl mb-6 sm:mb-12 tracking-tight font-luxury">
          <span className="font-light">Location de voitures</span> <span className="font-bold">à Taza</span>
        </h1>
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-24 sm:mb-32 font-light">
          Réservation rapide par appel, WhatsApp ou email.
        </p>

        {/* Animated Features Badge */}
        <div className="bg-black/40 border border-white/20 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 inline-flex items-center justify-center gap-3 sm:gap-4 shadow-2xl hover:shadow-[#fe7222]/20 transition-all duration-500 min-w-[280px] sm:min-w-[320px] h-[60px] sm:h-[70px] relative overflow-hidden">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 sm:gap-4 absolute transition-all ${currentFeature === index
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-3'
                }`}
              style={{
                transitionProperty: 'opacity, transform',
                transitionDuration: currentFeature === index ? '0ms' : '600ms',
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Icon with staggered animation */}
              <div
                className={`w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center transition-all ${currentFeature === index
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-2'
                  }`}
                style={{
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '400ms',
                  transitionDelay: currentFeature === index ? '0ms' : '0ms',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <i className={`${feature.icon} text-xl sm:text-2xl text-[#fe7222]`}></i>
              </div>

              {/* Text with staggered animation */}
              <span
                className={`text-sm sm:text-base md:text-lg font-medium whitespace-nowrap tracking-wide transition-all ${currentFeature === index
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-2'
                  }`}
                style={{
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '600ms',
                  transitionDelay: currentFeature === index ? '200ms' : '0ms',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {feature.text}
              </span>
            </div>
          ))}

          {/* Light sweep effect */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(254, 114, 34, 0.15) 50%, transparent 100%)',
              animation: 'lightSweep 3000ms cubic-bezier(0.4, 0, 0.2, 1) infinite'
            }}
          />
        </div>

        {/* Social Media Buttons */}
        <div className="mt-14 sm:mt-24 flex items-center justify-center gap-3 sm:gap-4 md:gap-5 flex-wrap">
          {contacts.map((contact, index) => (
            <a
              key={index}
              href={contact.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex-shrink-0"
            >
              <div className="relative bg-black/40 border border-white/20 rounded-full w-14 h-14 sm:w-16 sm:h-16 md:w-[68px] md:h-[68px] flex items-center justify-center transition-all duration-500 ease-out hover:bg-white/15 hover:border-white/30 hover:shadow-2xl hover:shadow-[#fe7222]/40 cursor-pointer">
                {/* Soft inner glow on hover */}
                <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${contact.color} opacity-0 group-hover:opacity-25 transition-opacity duration-500 ease-out`}></div>

                {/* Icon */}
                <i className={`${contact.icon} text-xl sm:text-2xl md:text-[26px] text-white relative z-10 transition-transform duration-300 ease-out group-hover:scale-105`}></i>
              </div>
            </a>
          ))}
        </div>

        {/* CTA Button */}
        <div className="mt-14 sm:mt-24 pb-8 sm:pb-0">
          <a
            href="tel:+212667304231"
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#fe7222] to-[#ff8c4a] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base md:text-lg font-semibold hover:shadow-2xl hover:shadow-[#fe7222]/40 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
              <i className="ri-phone-line text-lg sm:text-xl"></i>
            </div>
            Réserver maintenant
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-10 left-1/2 transform -translate-x-1/2 z-20 opacity-60 hover:opacity-100 transition-opacity">
        <div className="w-[30px] h-[50px] border-[1.5px] border-white/30 rounded-full flex justify-center p-2">
          <div className="w-1 h-3 bg-[#fe7222] rounded-full animate-bounce"></div>
        </div>
      </div>

      {/* Custom keyframes for light sweep */}
      <style>{`
        @keyframes lightSweep {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          20% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
}
